import {Injectable} from '@angular/core';
import {environment} from '../../environments/environment';
import {WarrantyList} from '../models/warranties/warranty-list.model';
import {Filter} from '../models/warranties/filter.model';
import { HttpClient } from '@angular/common/http';
import {CustomerDetails, Warranty} from '../models/warranties/warranty.model';
import {ContractDuration, ContractType} from '../models/warranties/contract-type.model';
import {ContractTypeDistinctModel} from '../models/warranties/contract-type-distinct.model';
import {Dealer} from '../models/dealers/dealer.model';
import {DealerFilter} from '../models/dealers/dealer-filter.model';
import {DealerList} from '../models/dealers/dealer-list.model';
import {ModelCode} from '../models/promotions/model-codes.model';
import {AdminPromotion} from '../models/promotions/admin-promotion.model';
import {Language} from '../models/disclaimer/language.model';
import {AdminContractType} from '../models/config/admin-contract-type.model';
import {AgeFactor} from '../models/config/age-factor.model';
import {MileageFactor} from '../models/config/mileage-factor.model';
import {ModelCodeList} from '../models/config/model-code-list.model';
import {ModelCodeFilter} from '../models/config/model-code-filter.model';
import {AdminActionPeriod} from '../models/action/admin-action-period.model';
import * as FileSaver from 'file-saver';
import {DealerData} from '../models/Report/dealerData.modal';
import {Model} from '../models/config/admin.model';
import {ModelList} from '../models/config/model-list.model';
import {ModelFilter} from '../models/config/model-filter.model';
import {AgeMileageConfiguration} from '../models/config/agemileageconfiguration.model';
import {AgeMileageMatrix} from '../models/config/agemileagematrix.model';
import {AgeMileageFilter} from '../models/config/agemileage-filter.model';
import {PriceMatrixFilter} from '../models/config/price-matrix-filter.model';
import {PricingDto} from '../models/config/pricing-dto.model';
import {CreditNote} from '../models/warranties/credit-note.model';
import {AuthService} from '../auth/auth.service';
import {FailedUploadData} from '../models/Report/failedUploadData.modal';


@Injectable({
  providedIn: 'root'
})
export class HttpService {

  baseUrl: string = environment.baseUrl;

  constructor(private http: HttpClient, private authService: AuthService) {
  }

  loginUserTest() {
    const d = {username: '', password: ''};
    return this.http.post(this.baseUrl + '/login', d);
  }

  loginUser(accessToken: string) {
    const d = {accessToken};
    return this.http.post(this.baseUrl + '/login', d);
  }

  searchForCustomer(firstName: string, lastName: string, buno: string) {
    const data = {firstName, lastName, buno};
    return this.http.post<CustomerDetails>(this.baseUrl + '/customer', data, this.getHeaders());
  }

  fetchContractTypesWithStartDate(brand: string, vin: string, orderDate: string, startDate: string, isExtended: boolean) {
    if (isExtended) {
      return this.http.get<ContractType[]>(this.baseUrl + '/warranty/contractTypes/extended/' + brand + '/' + vin
        + '/' + orderDate + '/' + startDate, this.getHeaders());
    } else {
      return this.http.get<ContractType[]>(this.baseUrl + '/warranty/contractTypes/' + brand + '/' + vin + '/'
        + orderDate + '/' + startDate, this.getHeaders());
    }
  }

  fetchContractTypes(brand: string, vin: string, orderDate: string, isExtended: boolean) {
    if (isExtended) {
      return this.http.get<ContractType[]>(this.baseUrl + '/warranty/contractTypes/extended/' + brand + '/' + vin + '/' + orderDate, this.getHeaders());
    } else {
      return this.http.get<ContractType[]>(this.baseUrl + '/warranty/contractTypes/' + brand + '/' + vin + '/' + orderDate, this.getHeaders());
    }
  }

  fetchDealerOptions() {
    return this.http.get<Dealer[]>(this.baseUrl + '/warranty/dealers', this.getHeaders());
  }

  fetchDistinctContractTypes() {
    return this.http.get<ContractTypeDistinctModel[]>(this.baseUrl + '/warranty/contractTypes', this.getHeaders());
  }

  fetchContractDurations() {
    return this.http.get<ContractDuration[]>(this.baseUrl + '/warranty/contractDurations', this.getHeaders());
  }

  fetchWarranties(filter: Filter) {
    return this.http.post<WarrantyList>(this.baseUrl + '/warranties', filter, this.getHeaders());
  }

  fetchAdminWarranties(filter: Filter) {
    return this.http.post<WarrantyList>(this.baseUrl + '/admin/warranties', filter, this.getHeaders());
  }

  fetchActiveContractTypes() {
    return this.http.get<ContractType[]>(this.baseUrl + '/admin/contracttypes', this.getHeaders());
  }

  fetchDashCounts() {
    return this.http.get(this.baseUrl + '/dashCounts', this.getHeaders());
  }

  fetchWarranty(id: number) {
    return this.http.get(this.baseUrl + '/warranty/' + id, this.getHeaders());
  }

  saveWarranty(warranty: Warranty) {
    return this.http.post(this.baseUrl + '/warranty', warranty, this.getHeaders());
  }

  submitWarranty(id: number) {
    return this.http.get(this.baseUrl + '/warranty/submit/' + id, this.getHeaders());
  }

  abortWarranty(id: number) {
    return this.http.get(this.baseUrl + '/warranty/abort/' + id, this.getHeaders());
  }

  refundWarranty(id: number, amount: number, notes: string) {
    const refundRequest = {
      warrantyId: id,
      refundAmount: amount,
      comments: notes
    };
    return this.http.post(this.baseUrl + '/creditnote/refund', refundRequest, this.getHeaders());
  }

  fetchVinAndCustomerData(vin: string, isExtended: boolean) {
    let str = 'new';
    if (isExtended) {
      str = 'extended';
    }
    return this.http.get(this.baseUrl + '/vin/' + str + '/' + vin, this.getHeaders());
  }

  fetchAcceptanceCriteria(contractCode: string) {
    return this.http.get(this.baseUrl + '/warranty/contractTypes/questions/' + contractCode, this.getHeaders());
  }

  checkIfWarrantyValuesExist(warranty: Warranty) {
    const d = {
      id: warranty.id,
      vin: warranty.vehicle.vin,
      contractCode: warranty.contract.contractCode,
      firstName: warranty.owner.firstName,
      lastName: warranty.owner.lastName,
      warrantyNumber: warranty.warrantyNumber
    };
    return this.http.post(this.baseUrl + '/warranty/cusVinCodeCheck/', d, this.getHeaders());
  }

  fetchBlacklistedVins(filter: Filter) {
    return this.http.post(this.baseUrl + '/admin/blacklist', filter, this.getHeaders());
  }

  addBlacklistVin(d) {
    return this.http.post(this.baseUrl + '/admin/blacklist/vin', d, this.getHeaders());
  }

  deleteBlacklistedVin(id: number) {
    return this.http.delete(this.baseUrl + '/admin/blacklist/vin/' + id, this.getHeaders());
  }

  fetchDealers() {
    return this.http.get<Dealer[]>(this.baseUrl + '/admin/dealers', this.getHeaders());
  }

  fetchDeliveringAgent() {
    return this.http.get<Dealer[]>(this.baseUrl + '/admin/dealers/deliveringAgent', this.getHeaders());
  }

  fetchDealersByContractType(contractType: string) {
    return this.http.get<Dealer[]>(this.baseUrl + '/warranty/dealers?contractType=' + contractType, this.getHeaders());
  }

  fetchDealerList(filter: DealerFilter) {
    return this.http.post<DealerList>(this.baseUrl + '/admin/dealerlist', filter, this.getHeaders());
  }

  addDealer(d: Dealer) {
    return this.http.post(this.baseUrl + '/admin/dealer', (d), this.getHeaders());
  }

  saveDealer(d: Dealer) {
    return this.http.put(this.baseUrl + '/admin/dealer', (d), this.getHeaders());
  }

  fetchPromotions() {
    return this.http.get(this.baseUrl + '/promotions', this.getHeaders());
  }

  fetchPromotionsRegions() {
    return this.http.get(this.baseUrl + '/admin/promotions/regions', this.getHeaders());
  }

  fetchPromotionsDealers() {
    return this.http.get(this.baseUrl + '/admin/promotions/dealers', this.getHeaders());
  }

  fetchPromotionContractTypes(brand: string) {
    return this.http.get<ContractType[]>(this.baseUrl + '/admin/promotions/contractTypes/' + brand, this.getHeaders());
  }

  fetchPromotionModels() {
    return this.http.get<ModelCode[]>(this.baseUrl + '/admin/promotions/modelCodes', this.getHeaders());
  }

  fetchAdminPromotions(filter: Filter) {
    return this.http.post(this.baseUrl + '/admin/promotions', filter, this.getHeaders());
  }

  fetchAdminPromotion(id: number) {
    return this.http.get<AdminPromotion>(this.baseUrl + '/admin/promotion/' + id, this.getHeaders());
  }

  savePromotion(promotion: AdminPromotion) {
    return this.http.post(this.baseUrl + '/admin/promotion', promotion, this.getHeaders());
  }

  deletePromotion(id: number) {
    return this.http.delete(this.baseUrl + '/admin/promotion/delete/' + id, this.getHeaders());
  }

  fetchActionPeriods() {
    return this.http.get(this.baseUrl + '/actionPeriods', this.getHeaders());
  }

  fetchActionPeriodsRegions() {
    return this.http.get(this.baseUrl + '/admin/actionPeriods/regions', this.getHeaders());
  }

  fetchActionPeriodsDealers() {
    return this.http.get(this.baseUrl + '/admin/actionPeriods/dealers', this.getHeaders());
  }

  fetchActionPeriodContractTypes(brand: string) {
    return this.http.get<ContractType[]>(this.baseUrl + '/admin/actionPeriods/contractTypes/' + brand, this.getHeaders());
  }

  fetchActionPeriodModels() {
    return this.http.get<ModelCode[]>(this.baseUrl + '/admin/actionPeriods/modelCodes', this.getHeaders());
  }

  fetchAdminActionPeriods(filter: Filter) {
    return this.http.post(this.baseUrl + '/admin/actionPeriods', filter, this.getHeaders());
  }

  fetchAdminActionPeriod(id: number) {
    return this.http.get<AdminActionPeriod>(this.baseUrl + '/admin/actionPeriod/' + id, this.getHeaders());
  }

  saveActionPeriod(actionPeriod: AdminActionPeriod) {
    return this.http.post(this.baseUrl + '/admin/actionPeriod', actionPeriod, this.getHeaders());
  }

  deleteActionPeriod(id: number) {
    return this.http.delete(this.baseUrl + '/admin/actionPeriod/delete/' + id, this.getHeaders());
  }

  postFiles(data: FormData, id: number) {
    return this.http.post(this.baseUrl + '/upload/' + id, data,
      {headers: {Authorization: sessionStorage.getItem('DWP_TOKEN')}});
  }

  removeFiles(id: number, files: string[]) {
    return this.http.post(this.baseUrl + '/files/remove/' + id, files, this.getHeaders());
  }

  postE4nFile(data: FormData) {
    return this.http.post(this.baseUrl + '/upload/e4n', data, this.getHeaders());
  }

  fetchDisclaimers() {
    return this.http.get<Language[]>(this.baseUrl + '/admin/disclaimer', this.getHeaders());
  }

  saveDisclaimers(data: Language[]) {
    return this.http.post(this.baseUrl + '/admin/disclaimer', data, this.getHeaders());
  }

  fetchHelpContent() {
    return this.http.get<Language[]>(this.baseUrl + '/admin/help', this.getHeaders());
  }

  saveHelpContent(data: Language[]) {
    return this.http.post(this.baseUrl + '/admin/help', data, this.getHeaders());
  }

  fetchAdminModelCodes(filter: ModelCodeFilter) {
    return this.http.post<ModelCodeList>(this.baseUrl + '/admin/config/modelCodes', filter, this.getHeaders());
  }

  saveModelCode(model: ModelCode) {
    return this.http.post(this.baseUrl + '/admin/config/modelCode', model, this.getHeaders());
  }

  saveModel(model: Model) {
    return this.http.post(this.baseUrl + '/admin/config/model', model, this.getHeaders());
  }

  fetchModels(modelFilter: ModelFilter) {
    return this.http.post<ModelList>(this.baseUrl + `/admin/config/models?page=${modelFilter.page}&size=${modelFilter.pageSize}`,
      modelFilter, this.getHeaders());
  }

  fetchAdminAgeFactors(year: number) {
    const d = {year};
    return this.http.post<AgeFactor[]>(this.baseUrl + '/admin/config/ageFactors', d, this.getHeaders());
  }

  saveAgeModel(model: AgeFactor) {
    return this.http.post(this.baseUrl + '/admin/config/ageFactor', model, this.getHeaders());
  }

  fetchAdminContractTypes() {
    return this.http.get<AdminContractType[]>(this.baseUrl + '/admin/config/contractTypes', this.getHeaders());
  }

  saveAdminContractType(model: AdminContractType) {
    return this.http.post(this.baseUrl + '/admin/config/contractType', model, this.getHeaders());
  }

  saveMileageFactor(model: MileageFactor, contractCode: string) {
    const d = {mileageFactor: model, contractCode: contractCode};
    return this.http.post(this.baseUrl + '/admin/config/mileageFactor', d, this.getHeaders());
  }

  getAvailableYears() {
    return this.http.get(this.baseUrl + '/admin/config/years', this.getHeaders());
  }

  fetchCalcLogs(vin: string) {
    const d = {vin: vin, page: 0, pageSize: 30};
    return this.http.post(this.baseUrl + '/admin/calcLog', d, this.getHeaders());
  }

  deleteMileageFactor(model: MileageFactor) {
    return this.http.post(this.baseUrl + '/admin/config/mileageFactor/delete', model, this.getHeaders());
  }

  fetchEstimations(est: any) {
    return this.http.post(this.baseUrl + '/warranty/estimate', est, this.getHeaders());
  }


  getHeaders() {
    return {headers: this.authService.getHeaders()};
  }

  downloadBooklet(d: string, name: string) {
    this.http.get(this.baseUrl + '/booklet/' + d, {
      headers: this.authService.getHeaders(),
      responseType: 'blob'
    })
      .subscribe((res: any) => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(res);
        a.download = name;
        a.click();
      });
  }

  downloadCertificate(d: string, name: string, language: string = 'NL') {
    this.http.get(this.baseUrl + '/certificate/' + d + '/' + language.toUpperCase(), {
      headers: this.authService.getHeaders(),
      responseType: 'blob'
    })
      .subscribe((res: any) => {
        if (res.size <= 0) {
          alert('No certificate found');
          return;
        }
        const a = document.createElement('a');
        a.href = URL.createObjectURL(res);
        a.download = name;
        a.click();
      });
  }

  downloadFile(id: number, d: string) {
    this.http.get(this.baseUrl + '/files/' + id + '/' + d, {
      headers: this.authService.getHeaders(),
      responseType: 'blob'
    })
      .subscribe((res: any) => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(res);
        a.download = d;
        a.click();
      });
  }

  fetchLatestE4nDate() {
    return this.http.get(this.baseUrl + '/admin/e4nDate', this.getHeaders());
  }

  downloadReport(filter: Filter) {
    const date = new Date();
    const yyyy = date.getFullYear();
    const mm = date.getMonth() + 1;
    const dd = date.getDate();
    const min = date.getMinutes();
    const hh = date.getHours();
    const ln = localStorage.getItem('selectedLanguage');
    return this.http.post<WarrantyList>(this.baseUrl + `/report/dealer/${ln}`, filter, {
      headers: {Authorization: sessionStorage.getItem('DWP_TOKEN')},
      responseType: 'blob' as 'json'
    }).subscribe((res: any) => {
      FileSaver.saveAs(res, `OverviewContracts_${yyyy}${mm}${dd}_${hh}${min}.xlsx`);
    });
  }

 downloadInProgressNinetyDaysPlusReport(filter: Filter){
    const date = new Date();
    const yyyy = date.getFullYear();
    const mm = date.getMonth() + 1;
    const dd = date.getDate();
    const min = date.getMinutes();
    const hh = date.getHours();
    const ln = localStorage.getItem('selectedLanguage');

    return this.http.post<WarrantyList>(this.baseUrl + `/report/dealer/in-progress/ninety-days/${ln}`, filter, {
      headers: {Authorization: sessionStorage.getItem('DWP_TOKEN')},
      responseType: 'blob' as 'json'
    }).subscribe((res: any) => {
      FileSaver.saveAs(res, `OverviewContracts_${yyyy}${mm}${dd}_${hh}${min}.xlsx`);
    });
  }

  downloadDealerReport(filter: any) {
    const ln = localStorage.getItem('selectedLanguage');
    return this.http.post<DealerData>(this.baseUrl + `/report/invoice/${ln}`, filter, {
      headers: {Authorization: sessionStorage.getItem('DWP_TOKEN')},
      responseType: 'blob' as 'json'
    }).subscribe((res: any) => {
      FileSaver.saveAs(res, `DealerInvoice_${filter.dealerNumber}_${filter.invoicedDate}.xlsx`);
    });
  }

  downloadFailedExportReport(filter: any, createdDate: string) {
    const ln = localStorage.getItem('selectedLanguage');
    return this.http.post<FailedUploadData>(this.baseUrl + `/report/failed-upload/${ln}`, filter, {
      headers: {Authorization: sessionStorage.getItem('DWP_TOKEN')},
      responseType: 'blob' as 'json'
    }).subscribe((res: any) => {
      FileSaver.saveAs(res, `FailedUpload_${createdDate}.xlsx`);
    });
  }

  downloadDealerCreditNoteReport(filter: any) {
    const ln = localStorage.getItem('selectedLanguage');
    return this.http.post<DealerData>(this.baseUrl + `/report/credit-note/${ln}`, filter, {
      headers: {Authorization: sessionStorage.getItem('DWP_TOKEN')},
      responseType: 'blob' as 'json'
    }).subscribe((res: any) => {
      FileSaver.saveAs(res, `DealerCreditNote_${filter.dealerNumber}_${filter.exportDate}.xlsx`);
    });
  }

  downloadWarrantyReport(filter: any) {
    const date = new Date();
    const yyyy = date.getFullYear();
    const mm = date.getMonth() + 1;
    const dd = date.getDate();
    const min = date.getMinutes();
    const hh = date.getHours();
    const ln = localStorage.getItem('selectedLanguage');
    return this.http.post<DealerData>(this.baseUrl + `/report/warranty/${ln}`, filter, {
      headers: this.authService.getHeaders(),
      responseType: 'blob' as 'json'
    }).subscribe((res: any) => {
      FileSaver.saveAs(res, `WarrantySummary_${yyyy}${mm}${dd}_${hh}${min}.xlsx`);
    });
  }

  downloadAgeReport(filter: any) {
    const date = new Date();
    const yyyy = date.getFullYear();
    const mm = date.getMonth() + 1;
    const dd = date.getDate();
    const min = date.getMinutes();
    const hh = date.getHours();
    const ln = localStorage.getItem('selectedLanguage');
    return this.http.post<DealerData>(this.baseUrl + `/report/ageBreakdown/${ln}`, filter, {
      headers: this.authService.getHeaders(),
      responseType: 'blob' as 'json'
    }).subscribe((res: any) => {
      FileSaver.saveAs(res, `AgeBreakdown_${yyyy}${mm}${dd}_${hh}${min}.xlsx`);
    });
  }

  getWarrantyReport(filter) {
    return this.http.post<WarrantyList>(this.baseUrl + '/report/warranty', filter, this.getHeaders());
  }

  getInvoice(filter) {
    return this.http.post<DealerData>(this.baseUrl + '/report/invoice', filter, this.getHeaders());
  }

  getFailedUploadReportItems(filter) {
    return this.http.post<FailedUploadData>(this.baseUrl + '/report/failed-upload', filter, this.getHeaders());
  }

  getCreditNoteReportItems(filter) {
    return this.http.post<DealerData>(this.baseUrl + '/report/credit-note', filter, this.getHeaders());
  }

  getAgeReport(filter) {
    return this.http.post<any>(this.baseUrl + '/report/ageBreakdown', filter, this.getHeaders());
  }

  downloadFullExport() {
    return this.http.get<any>(this.baseUrl + '/report/full-export', {
      headers: {Authorization: sessionStorage.getItem('DWP_TOKEN')},
      responseType: 'blob' as 'json'
    }).subscribe((res: any) => {
      FileSaver.saveAs(res, `full_export.xlsx`);
    });
  }

  fullExport() {
    return this.http.get<any>(this.baseUrl + '/report/full-export', this.getHeaders());
  }

  getMinimumFee(code, date) {
    return this.http.get<any>(this.baseUrl + `/contract/minimumFee?code=${code}&date=${date}`, this.getHeaders());
  }

  fetchAgeMileageMatrix(filter: AgeMileageFilter) {
    return this.http.post<AgeMileageMatrix>(this.baseUrl + '/admin/config/age-mileage-matrix', filter, this.getHeaders());
  }

  saveAgeMileageConfiguration(configuration: AgeMileageConfiguration) {
    return this.http.post(this.baseUrl + '/admin/config/age-mileage-configuration', configuration, this.getHeaders());
  }

  getPricingMatrix(filter: PriceMatrixFilter) {
    return this.http.post<PricingDto[]>(this.baseUrl + '/admin/config/price-matrix', filter, this.getHeaders());
  }

  savePricingMatrix(matrix: Array<PricingDto>, year: number) {
    return this.http.post<PricingDto[]>(this.baseUrl + `/admin/config/price-matrix/year/${year}`, matrix, this.getHeaders());
  }

  getCreditNote(warrantyId: number) {
    return this.http.get<CreditNote>(this.baseUrl + `/warranty/${warrantyId}/credit-note`, this.getHeaders());
  }

  fetchWarrantiesNinetyDaysPlusInProgress(filter: Filter) {
    return this.http.post<WarrantyList>(this.baseUrl + '/warranties/in-progress/ninetyplus', filter, this.getHeaders());
  }

  readWarrantyConditionBooklets(){
    return this.http.get<any>(this.baseUrl + '/booklets/list-warranty-conditions-structure', this.getHeaders());
  }

  downloadWarrantyConditionBooklet(folder: string, filenameAndExtension: string){
    this.http.get(this.baseUrl + '/file/warranty-conditions/' + folder + '/' + filenameAndExtension, {
      headers: this.authService.getHeaders(),
      responseType: 'blob'
    })
      .subscribe((res: any) => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(res);
        a.download = filenameAndExtension;
        a.click();
      });
  }

  deleteWarrantyConditionBooklet(folder: string, filenameAndExtension: string){
    return this.http.delete(this.baseUrl + '/file/delete/warranty-conditions/' + folder + '/'
      + filenameAndExtension, {headers: {Authorization: sessionStorage.getItem('DWP_TOKEN')}});
  }

  postWarrantyConditions(folder: string, data: FormData) {
    return this.http.post(this.baseUrl + '/upload/warranty-conditions/' + folder, data,
      {headers: {Authorization: sessionStorage.getItem('DWP_TOKEN')}});
  }
  postBulkContractCSV(folder: string, data: FormData) {
    const ln = localStorage.getItem('selectedLanguage');
    return this.http.post(this.baseUrl + `/upload/bulk/contracts`, data,
      {headers: {Authorization: sessionStorage.getItem('DWP_TOKEN')}});
  }
  createWarrantyConditionsCategory(folder: string) {
    return this.http.get(this.baseUrl + '/create/warranty-conditions/' + folder,
      {headers: {Authorization: sessionStorage.getItem('DWP_TOKEN')}});
  }

  deleteWarrantyConditionsFolder(folder: string) {
    return this.http.delete(this.baseUrl + '/delete/warranty-conditions/' + folder,
      {headers: {Authorization: sessionStorage.getItem('DWP_TOKEN')}});
  }
}
